"use client";

import { useEffect, useRef, useCallback, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSesionDeFirebase } from "src/hooks/useSesionDeFirebase";
import { fetchConToken } from "src/services/fetchConToken";
import {
  collaborator_session_login_url,
  collaborator_session_logout_url,
  collaborator_session_pause_url,
  collaborator_session_unpause_url,
  collaborator_session_status_url,
  collaborator_session_base_url,
} from "@/config/endpoints";

type SessionStatus = "active" | "paused" | "offline";

interface SessionBase {
  id: number;
  name: string;
  CRPC?: string | null;
}

interface StatusResponse {
  status: SessionStatus;
  base_id: number | null;
  base: SessionBase | null;
}

async function postSession(url: string, params?: Record<string, string>) {
  const queryStr = params ? `?${new URLSearchParams(params)}` : "";
  const response = await fetchConToken(`${url}${queryStr}`, { method: "POST" });
  if (!response.ok) throw new Error(`Session error: ${response.status}`);
  return response.json();
}

async function fetchStatus(): Promise<StatusResponse> {
  const response = await fetchConToken(collaborator_session_status_url, {
    cache: "no-store",
  });
  if (!response.ok) throw new Error(`Status error: ${response.status}`);
  return response.json();
}

export function useCollaboratorSession(initialBaseId?: number) {
  const { hayUsuario } = useSesionDeFirebase();
  const queryClient = useQueryClient();
  const hasLoggedIn = useRef(false);
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  /**
   * La consulta espera a que Firebase restaure la sesión, no a tener un token
   * en mano: el que viaja lo pide `fetchConToken` en el momento de la
   * llamada.
   */
  const statusQuery = useQuery({
    queryKey: ["collaboratorSessionStatus"],
    enabled: hayUsuario,
    queryFn: fetchStatus,
    refetchInterval: 30_000,
    staleTime: 15_000,
  });

  /**
   * `statusConocido` distingue "sé que no está trabajando" de "no pude
   * averiguarlo".
   *
   * `status` cae en `"offline"` cuando la consulta no resolvió —o no corre,
   * porque está gateada en que Firebase haya restaurado la sesión— y el
   * sidebar escondía sus controles con ese valor. El 8/9 una enfermera con
   * turno **activo en la base** no veía el indicador de disponibilidad ni podía
   * declarar base: su navegador no estaba consultando el estado, y la pantalla
   * no daba ninguna señal. El endpoint estaba sano, 998 respuestas 200 en esos
   * mismos minutos.
   *
   * Esconder un control porque no se pudo leer su estado deja a la persona sin
   * forma de arreglarlo y sin saber que hay algo que arreglar.
   */
  const statusConocido = statusQuery.data !== undefined;
  const status: SessionStatus = statusQuery.data?.status ?? "offline";
  const currentBaseId = statusQuery.data?.base_id ?? null;
  const currentBase = statusQuery.data?.base ?? null;

  const invalidateStatus = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["collaboratorSessionStatus"] });
  }, [queryClient]);

  const loginMutation = useMutation({
    mutationFn: (baseId?: number) => {
      const params: Record<string, string> = {};
      const bid = baseId ?? initialBaseId;
      if (bid) params.base_id = String(bid);
      return postSession(collaborator_session_login_url, params);
    },
    onSuccess: invalidateStatus,
  });

  const logoutMutation = useMutation({
    mutationFn: () => postSession(collaborator_session_logout_url),
    onSuccess: invalidateStatus,
  });

  const pauseMutation = useMutation({
    mutationFn: () => postSession(collaborator_session_pause_url),
    onSuccess: invalidateStatus,
  });

  const unpauseMutation = useMutation({
    mutationFn: () => postSession(collaborator_session_unpause_url),
    onSuccess: invalidateStatus,
  });

  const changeBaseMutation = useMutation({
    mutationFn: (baseId: number) =>
      fetchConToken(collaborator_session_base_url, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ base_id: baseId }),
      }).then((r) => {
        if (!r.ok) throw new Error(`Change base error: ${r.status}`);
        return r.json();
      }),
    onSuccess: invalidateStatus,
  });

  // Auto-login on mount
  useEffect(() => {
    if (!hayUsuario || hasLoggedIn.current || isLoggingIn) return;
    if (statusQuery.isLoading) return;

    if (status === "offline") {
      setIsLoggingIn(true);
      hasLoggedIn.current = true;
      loginMutation.mutate(undefined, {
        onSettled: () => setIsLoggingIn(false),
      });
    } else {
      hasLoggedIn.current = true;
    }
  }, [hayUsuario, status, statusQuery.isLoading]);

  // Auto-logout on unmount / beforeunload
  useEffect(() => {
    if (!hayUsuario) return;

    /**
     * Ojo: `sendBeacon` no deja poner encabezados, así que este logout sale
     * sin `Authorization` y el backend lo rechaza con 401 siempre. Al cerrar
     * la pestaña el turno queda abierto y lo termina cerrando el corte de 16
     * horas. Se arregla aparte: el reemplazo por `fetch` con `keepalive`
     * tiene su propio riesgo y no corresponde meterlo acá.
     */
    const handleBeforeUnload = () => {
      navigator.sendBeacon(
        collaborator_session_logout_url,
        new Blob([JSON.stringify({})], { type: "application/json" })
      );
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      if (hasLoggedIn.current) {
        logoutMutation.mutate();
      }
    };
  }, [hayUsuario]);

  const togglePause = useCallback(() => {
    if (status === "active") {
      pauseMutation.mutate();
    } else if (status === "paused") {
      unpauseMutation.mutate();
    }
  }, [status]);

  const changeBase = useCallback(
    (baseId: number) => {
      changeBaseMutation.mutate(baseId);
    },
    [changeBaseMutation],
  );

  return {
    status,
    statusConocido,
    isOnline: status !== "offline",
    isPaused: status === "paused",
    togglePause,
    isLoading: statusQuery.isLoading || isLoggingIn,
    currentBaseId,
    currentBase,
    changeBase,
    loginWithBase: (baseId: number) => loginMutation.mutate(baseId),
  };
}
